import React, { Component } from 'react';
import { Play, Shield, Eye, Clock, Users, CheckCircle, Star, ArrowRight, Phone, Mail, MapPin, HardHat, Truck, AlertTriangle, Camera, Wrench, Building } from 'lucide-react';

class Construction extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeFeature: 0,
      stats: [
        { number: '85%', label: 'Theft Reduction' },
        { number: '24/7', label: 'Site Monitoring' },
        { number: '100+', label: 'Clients Protected' },
        { number: '<2min', label: 'Response Time' }
      ],
      features: [
        {
          icon: <Camera className="w-6 h-6" />,
          title: 'AI-Powered Theft Detection',
          description: 'Advanced computer vision technology detects unauthorized access and equipment theft in real-time.'
        },
        {
          icon: <Shield className="w-6 h-6" />,
          title: 'Perimeter Security Monitoring',
          description: 'Comprehensive fence line and boundary monitoring with instant alerts for security breaches.'
        },
        {
          icon: <HardHat className="w-6 h-6" />,
          title: 'Worker Safety Compliance',
          description: 'Monitor PPE compliance and safety protocols to ensure regulatory adherence and prevent accidents.'
        },
        {
          icon: <Eye className="w-6 h-6" />,
          title: 'Progress Documentation',
          description: 'Time-lapse construction documentation for project management and insurance purposes.'
        }
      ],
      services: [
        {
          title: 'Equipment & Material Protection',
          description: 'Safeguard valuable construction equipment and materials from theft and vandalism.',
          features: ['Heavy machinery monitoring', 'Material stockpile surveillance', 'Tool trailer security']
        },
        {
          title: 'Site Access Control',
          description: 'Control and monitor who enters and exits your construction site.',
          features: ['Visitor management system', 'Contractor credential verification', 'After-hours access alerts']
        },
        {
          title: 'Safety Monitoring',
          description: 'Ensure compliance with safety regulations and prevent workplace accidents.',
          features: ['PPE compliance checking', 'Hazard zone monitoring', 'Emergency response coordination']
        },
        {
          title: 'Mobile Surveillance Units',
          description: 'Flexible security solutions for remote and temporary construction sites.',
          features: ['Solar-powered cameras', 'Cellular connectivity', 'Rapid deployment capability']
        }
      ],
      
      challenges: [
        {
          icon: <AlertTriangle className="w-6 h-6" />,
          title: 'Equipment Theft',
          description: 'Construction sites lose billions annually to equipment and material theft.'
        },
        {
          icon: <Users className="w-6 h-6" />,
          title: 'Unauthorized Access',
          description: 'Trespassers and vandals pose significant risks to project timelines and safety.'
        },
        {
          icon: <Clock className="w-6 h-6" />,
          title: 'After-Hours Incidents',
          description: 'Most construction site crimes occur during off-hours when sites are unattended.'
        },
        {
          icon: <Building className="w-6 h-6" />,
          title: 'Safety Compliance',
          description: 'Maintaining CCOHS compliance and preventing workplace accidents is critical.'
        }
      ],
      testimonials: [
        {
          name: 'Robert Martinez',
          company: 'Summit Construction Co.',
          rating: 5,
          text: 'Reduced equipment theft by 90% and improved our site safety compliance significantly.'
        },
        {
          name: 'Jennifer Walsh',
          company: 'Metro Development',
          rating: 5,
          text: 'The AI detection system caught break-ins we would have missed. Excellent ROI on security investment.'
        },
        {
          name: 'David Thompson',
          company: 'Skyline Builders',
          rating: 5,
          text: 'Professional service with rapid response times. Our insurance premiums decreased after implementation.'
        }
      ],
    };
  }

  componentDidMount() {
    // Auto-rotate features every 4 seconds
    this.featureInterval = setInterval(() => {
      this.setState(prevState => ({
        activeFeature: (prevState.activeFeature + 1) % prevState.features.length
      }));
    }, 4000);
  }

  componentWillUnmount() {
    if (this.featureInterval) {
      clearInterval(this.featureInterval);
    }
  }

  setActiveFeature = (index) => {
    this.setState({ activeFeature: index });
  }

  render() {
    const { activeFeature, stats, features, services, testimonials, challenges } = this.state;

    const styles = {
      root: {
        '--primary-dark': '#010A1A',
        '--secondary-dark': '#002C58',
        '--text-primary': '#ffffff',
        '--text-secondary': '#94a3b8',
        '--box-dark-bg': '#1a2332',
        '--icons-color': '#155EEF',
        '--accent-cyan': '#06b6d4',
        '--accent-blue': '#3b82f6'
      }
    };

    return (
      <div className="min-vh-100" 
           style={{
             backgroundColor: 'var(--primary-dark)',
             color: 'var(--text-primary)',
             fontFamily: 'system-ui, -apple-system, sans-serif'
           }}>
        
       

        <section className="general-banner">
          <div className="container">
            <div className="general-content">
              <h1 className="hero-title orbitron">Construction</h1>
              <p className="hero-subtitle">Enhancing construction site safety, access control, and progress visibility with 24/7 live monitoring, AI-powered analytics, and time-lapse video solutions.</p>
            </div>
          </div>
        </section>

      

        {/* Industry Challenges Section */}
        <section className="py-5">
          <div className="container">
            <div className="row mb-5">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Construction Industry Challenges
                </h2>
                <p className="lead" style={{ color: 'var(--text-secondary)' }}>
                  Understanding the unique security risks facing construction sites
                </p>
              </div>
            </div>
            <div className="row g-4">
              {challenges.map((challenge, index) => (
                <div key={index} className="col-lg-6 col-md-6">
                  <div className="p-4 h-100 rounded-3" 
                       style={{
                         backgroundColor: 'var(--box-dark-bg)',
                         border: '1px solid var(--secondary-dark)'
                       }}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="p-3 rounded-2 flex-shrink-0" 
                           style={{ 
                             backgroundColor: '#dc3545', 
                             color: 'white' 
                           }}>
                        {challenge.icon}
                      </div>
                      <div>
                        <h4 className="fw-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                          {challenge.title}
                        </h4>
                        <p className="mb-0" style={{ color: 'var(--text-secondary)' }}>
                          {challenge.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-5" style={{ backgroundColor: 'var(--box-dark-bg)' }}>
          <div className="container">
            <div className="row mb-5">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Why Choose Our Construction Security?
                </h2>
                <p className="lead" style={{ color: 'var(--text-secondary)' }}>
                  Proven results that protect your investment and improve project outcomes
                </p>
              </div>
            </div>
            <div className="row g-4">
              <div className="col-lg-4 col-md-6">
                <div className="text-center p-4">
                  <div className="mb-3">
                    <Shield className="w-16 h-16 mx-auto" style={{ color: 'var(--accent-cyan)' }} />
                  </div>
                  <h4 className="fw-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Reduce Theft by 85%
                  </h4>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Our AI-powered detection systems dramatically reduce equipment and material theft.
                  </p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="text-center p-4">
                  <div className="mb-3">
                    <Clock className="w-16 h-16 mx-auto" style={{ color: 'var(--accent-blue)' }} />
                  </div>
                  <h4 className="fw-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Faster Response Times
                  </h4>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Immediate alerts and rapid response minimize damage and prevent incidents.
                  </p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="text-center p-4">
                  <div className="mb-3">
                    <Users className="w-16 h-16 mx-auto" style={{ color: 'var(--icons-color)' }} />
                  </div>
                  <h4 className="fw-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Improve Safety Compliance
                  </h4>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Monitor safety protocols and maintain CCOHS compliance automatically.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Features Section */}
        {/* <section className="py-5" style={{ backgroundColor: 'var(--box-dark-bg)' }}>
          <div className="container">
            <div className="row mb-5">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Advanced Security Solutions
                </h2>
                <p className="lead" style={{ color: 'var(--text-secondary)' }}>
                  AI-powered technology designed specifically for construction environments
                </p>
              </div>
            </div>
            <div className="row g-4">
              {features.map((feature, index) => (
                <div key={index} className="col-lg-6 col-md-6">
                  <div className={`p-4 h-100 rounded-3 cursor-pointer transition-all ${
                         activeFeature === index ? 'border-2' : 'border-1'
                       }`}
                       style={{
                         backgroundColor: activeFeature === index ? 'var(--secondary-dark)' : 'var(--primary-dark)',
                         borderColor: activeFeature === index ? 'var(--accent-blue)' : 'var(--secondary-dark)',
                         transition: 'all 0.3s ease',
                         cursor: 'pointer'
                       }}
                       onClick={() => this.setActiveFeature(index)}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="p-3 rounded-2 flex-shrink-0" 
                           style={{ 
                             backgroundColor: 'var(--icons-color)', 
                             color: 'white' 
                           }}>
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="fw-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                          {feature.title}
                        </h4>
                        <p className="mb-0" style={{ color: 'var(--text-secondary)' }}>
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Services Section */}
        <section className="py-5">
          <div className="container">
            <div className="row mb-5">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Our Construction Security Services
                </h2>
                <p className="lead" style={{ color: 'var(--text-secondary)' }}>
                  Comprehensive protection solutions tailored for construction sites
                </p>
              </div>
            </div>
            <div className="row g-4">
              {services.map((service, index) => (
                <div key={index} className="col-lg-6">
                  <div className="p-4 h-100 rounded-3" 
                       style={{
                         backgroundColor: 'var(--box-dark-bg)',
                         border: '1px solid var(--secondary-dark)'
                       }}>
                    <h4 className="fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                      {service.title}
                    </h4>
                    <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>
                      {service.description}
                    </p>
                    <ul className="list-unstyled">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="d-flex align-items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 flex-shrink-0" 
                                      style={{ color: 'var(--accent-cyan)' }} />
                          <span style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>



        {/* Testimonials Section */}
        <section className="py-5">
          <div className="container">
            <div className="row mb-5">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  What Our Construction Clients Say
                </h2>
                <p className="lead" style={{ color: 'var(--text-secondary)' }}>
                  Real results from construction companies across the industry
                </p>
              </div>
            </div>
            <div className="row g-4">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="col-lg-4">
                  <div className="p-4 h-100 rounded-3" 
                       style={{
                         backgroundColor: 'var(--box-dark-bg)',
                         border: '1px solid var(--secondary-dark)'
                       }}>
                    <div className="d-flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4" 
                              style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                      ))}
                    </div>
                    <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>
                      "{testimonial.text}"
                    </p>
                    <div>
                      <h6 className="fw-bold mb-1" style={{ color: 'var(--text-primary)' }}>
                        {testimonial.name}
                      </h6>
                      <small style={{ color: 'var(--text-secondary)' }}>
                        {testimonial.company}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-5" 
                 style={{
                   background: `linear-gradient(135deg, var(--secondary-dark) 0%, var(--accent-blue) 100%)`
                 }}>
          <div className="container">
            <div className="row">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Protect Your Construction Investment
                </h2>
                <p className="lead mb-4" style={{ color: 'var(--text-primary)' }}>
                  Get a free security assessment and see how much you can save with our construction security solutions
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  <button className="btn btn-light btn-lg px-4 py-2 d-flex align-items-center gap-2">
                    <Phone className="w-4 h-4" /> Call Now: +1 (403) 992-1672
                  </button>
                  {/* <button className="btn btn-outline-light btn-lg px-4 py-2 d-flex align-items-center gap-2">
                    <Mail className="w-4 h-4" /> Get Free Assessment
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default Construction;